import React from 'react';
import { Box, Paper, Typography, Link } from '@mui/material';
import PageGuideTooltip from './PageGuideTooltip';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import RawAdjustedToggle from './RawAdjustedToggle';
import WinRateWithOpen from './WinRateWithOpen';
import { fmtOpenShare } from '../utils/winRate';

export default function WinRateLiftCard({
  attendeeWinRate, attendeeN, attendeeOpenShare,
  baselineWinRate, baselineN, baselineOpenShare,
  regression,
  onClick,
}) {
  const navigate = useNavigate();
  const [mode, setMode] = React.useState('adjusted');
  const ame = regression?.ame_pts;
  const ameAvailable = ame != null && !regression?.insufficient_data && !regression?.fit_error;
  const showAdjusted = ameAvailable && mode === 'adjusted';
  const ameColor = showAdjusted ? (ame >= 0 ? '#2e7d32' : '#e65100') : 'text.secondary';

  const tooltipText = showAdjusted
    ? "Regression-adjusted win rate lift controlling for deal size at creation, product family, industry, and geography. Trained on closed opportunities created in the baseline's window (not date-filtered); deals still open are not in the model. The attendee win rate shown below does respect the selected date range. Baseline excludes all accounts with any TEC workshop participant. Click to see raw rates and full methodology."
    : "Attendee win rate — closed won ÷ (won + lost) for TEC attendees' opportunities in the selected date range. Deals still open are not in the rate; the open share shows how many are undecided. The regression lift % (when available) is trained on all-time closed data. Baseline excludes all accounts with any TEC workshop participant. Click for baseline comparison and details.";

  return (
    <Paper
      elevation={2}
      aria-label="Win Rate Lift card"
      onClick={onClick}
      sx={{
        p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1,
        ...(onClick ? { cursor: 'pointer', '&:hover': { boxShadow: 4 } } : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Win Rate Lift
        </Typography>
        <PageGuideTooltip title="Win rate lift" description={tooltipText} metricKey="win_rate_lift_pts" />
      </Box>

      {ameAvailable && (
        <Box sx={{ alignSelf: 'flex-start' }}>
          <RawAdjustedToggle
            value={mode}
            onChange={setMode}
            ariaLabel="show raw or adjusted win rate"
          />
        </Box>
      )}

      {showAdjusted ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {ame >= 0 && <CheckCircleOutlineIcon sx={{ fontSize: '0.9rem', color: '#2e7d32' }} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Adjusted for deal complexity
            </Typography>
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: ameColor }}>
            {ame >= 0 ? '+' : ''}{ame} pp
          </Typography>
          {regression.ci_lo_pts != null && regression.ci_hi_pts != null && (
            <Typography variant="caption" color="text.secondary">
              95% CI: {regression.ci_lo_pts >= 0 ? '+' : ''}{regression.ci_lo_pts} to {regression.ci_hi_pts >= 0 ? '+' : ''}{regression.ci_hi_pts} pp
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Controls for deal size at creation, product, industry, geography{regression.training_window?.created_from ? ` · deals created since ${regression.training_window.created_from}` : ''}{fmtOpenShare(attendeeOpenShare) ? ` · attendees ${fmtOpenShare(attendeeOpenShare)}` : ''}
          </Typography>
        </>
      ) : (
        <>
          <WinRateWithOpen rate={attendeeWinRate} n={attendeeN} openShare={attendeeOpenShare} variant="h4" />
          <Typography variant="caption" color="text.secondary">Attendee win rate</Typography>
          {baselineWinRate !== undefined && (
            <WinRateWithOpen rate={baselineWinRate} n={baselineN} openShare={baselineOpenShare} variant="body2" label="baseline" />
          )}
        </>
      )}

      {onClick && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 'auto', fontStyle: 'italic' }}>
          Click for raw rates & methodology
        </Typography>
      )}
      <Link
        component="button"
        variant="caption"
        onClick={e => { e.stopPropagation(); navigate('/methodology#win-rate-statistical-rigor'); }}
        sx={{ alignSelf: 'flex-start', fontWeight: 600, cursor: 'pointer', color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
      >
        Full methodology →
      </Link>
    </Paper>
  );
}
